import React, { useState, useEffect, useContext } from 'react';
import { getData, postData, serverURL } from '../../../../services/Fetchnodeservices';
// ⚠️ IMPORTANT: Adjust this path to exactly match where your ThemeContext is located!
import { ThemeContext } from '../../../../context/ThemeContext'; 

export default function PressReleaseManager() {
  // 🛡️ SAFE CONTEXT CONSUMPTION WITH FALLBACK
  const themeContext = useContext(ThemeContext);
  const isDarkMode = themeContext?.currentTheme === 'dark';
  
  const fallbackTheme = {
    background: isDarkMode ? "#0c1018" : "#f8fafc",
    surface: isDarkMode ? "#141824" : "#ffffff",
    border: isDarkMode ? "rgba(255,255,255,0.07)" : "#e2e8f0",
    textPrimary: isDarkMode ? "#f1f5f9" : "#0f172a",
    textSecondary: isDarkMode ? "#94a3b8" : "#64748b",
    textMuted: isDarkMode ? "#64748b" : "#94a3b8",
    primary: "#6366f1",
    primaryLight: "rgba(99, 102, 241, 0.15)",
    primaryGradient: "linear-gradient(135deg, #6366f1, #8b5cf6)",
  };

  const brandTheme = themeContext?.theme || fallbackTheme;
  
  // 🔴 UNIFIED THEME OBJECT
  const theme = {
    bg: brandTheme.background,
    surface: brandTheme.surface,
    surfaceAlt: isDarkMode ? "#1e293b" : "#f1f5f9",
    border: brandTheme.border,
    text: brandTheme.textPrimary,
    textMuted: brandTheme.textMuted,
    primary: brandTheme.primary,
    primaryLight: brandTheme.primaryLight || `${brandTheme.primary}25`,
    gradient: brandTheme.primaryGradient || `linear-gradient(135deg, ${brandTheme.primary}, ${brandTheme.primary}dd)`,
    success: "#22c55e",
    successLight: isDarkMode ? "rgba(34,197,94,0.2)" : "rgba(34,197,94,0.1)",
    danger: "#ef4444",
    dangerLight: isDarkMode ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.1)",
    warning: "#f59e0b",
    warningLight: isDarkMode ? "rgba(245,158,11,0.2)" : "rgba(245,158,11,0.1)",
    info: brandTheme.primary,
  };

  const [pressReleases, setPressReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    rd: "",
    status: "1",
    hide: "0",
  });
  const [pic, setPic] = useState(null);
  const [preview, setPreview] = useState("");

  // Fetch all press releases on component mount
  useEffect(() => {
    fetchPressReleases();
  }, []);

  const fetchPressReleases = async () => {
    try {
      setLoading(true);
      const response = await getData("pressrelase/fetch_all_press_release");
      if (response.status) {
        setPressReleases(response.data || []);
      } else {
        console.error("Failed to fetch:", response.message);
        setPressReleases([]);
      }
    } catch (error) {
      console.error("Error fetching press releases:", error);
      setPressReleases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPic(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      rd: "",
      status: "1",
      hide: "0",
    });
    setPic(null);
    setPreview("");
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) { alert("Please enter title"); return; }
    if (!formData.description.trim()) { alert("Please enter description"); return; }
    if (!formData.rd.trim()) { alert("Please enter RD"); return; }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("rd", formData.rd);
      formDataToSend.append("status", formData.status);
      formDataToSend.append("hide", formData.hide);
      if (pic) formDataToSend.append("pic", pic);
      if (editingId) formDataToSend.append("id", editingId);

      const endpoint = editingId ? "pressrelase/update_press_release" : "pressrelase/add_press_release";
      const result = await postData(endpoint, formDataToSend);
      alert(result.message);

      if (result.status) {
        fetchPressReleases();
        setShowModal(false);
        resetForm();
      }
    } catch (e) {
      console.log(e);
      alert("Something went wrong");
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title || "",
      description: item.description || "",
      rd: item.rd || "",
      status: item.status?.toString() || "1",
      hide: item.hide?.toString() || "0",
    });
    if (item.pic) {
      const imagePath = `${serverURL}/uploadnew/upload/${item.pic}`;
      setPreview(imagePath);
    } else {
      setPreview("");
    }
    setPic(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this press release?")) {
      try {
        const result = await postData("pressrelase/delete_press_release", { id });
        alert(result.message);
        if (result.status) {
          fetchPressReleases();
        }
      } catch (error) {
        console.error("Delete error:", error);
        alert("Failed to delete");
      }
    }
  };

  const getImageUrl = (picPath) => {
    if (!picPath) return null;
    return `${serverURL}/uploadnew/upload/${picPath}`;
  };

  const getStatusBadge = (status) => {
    const isActive = status == 1;
    return (
      <span style={{
        display: "inline-block",
        backgroundColor: isActive ? theme.successLight : theme.dangerLight,
        color: isActive ? theme.success : theme.danger,
        padding: "4px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
      }}>
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  const getHideBadge = (hide) => {
    const isHidden = hide == 1;
    return (
      <span style={{
        display: "inline-block",
        backgroundColor: isHidden ? theme.warningLight : theme.successLight,
        color: isHidden ? theme.warning : theme.success,
        padding: "4px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
      }}>
        {isHidden ? "Hidden" : "Visible"}
      </span>
    );
  };

  const styles = {
    container: {
      maxWidth: "1400px",
      margin: "0 auto",
      padding: "24px",
      fontFamily: "'Inter', 'DM Sans', sans-serif",
      backgroundColor: theme.bg,
      minHeight: "100vh",
      transition: "background-color 0.3s ease",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "15px",
    },
    title: {
      fontSize: "28px",
      color: theme.text,
      margin: 0,
      fontWeight: "700",
      letterSpacing: "-0.5px",
    },
    addButton: {
      backgroundColor: theme.gradient,
      color: "white",
      border: "none",
      padding: "10px 20px",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s",
      boxShadow: `0 4px 6px -1px ${theme.primary}30`,
    },
    tableWrapper: {
      backgroundColor: theme.surface,
      borderRadius: "12px",
      border: `1px solid ${theme.border}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      overflowX: "auto",
      transition: "all 0.3s ease",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
      minWidth: "900px",
    },
    th: {
      padding: "14px 16px",
      textAlign: "left",
      backgroundColor: theme.surfaceAlt,
      borderBottom: `1px solid ${theme.border}`,
      fontWeight: "600",
      color: theme.primary,
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    td: {
      padding: "14px 16px",
      borderBottom: `1px solid ${theme.border}`,
      verticalAlign: "middle",
      color: theme.text,
    },
    tdTitle: {
      padding: "14px 16px",
      borderBottom: `1px solid ${theme.border}`,
      fontWeight: "600",
      color: theme.text,
      maxWidth: "200px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    tdDesc: {
      padding: "14px 16px",
      borderBottom: `1px solid ${theme.border}`,
      maxWidth: "250px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: theme.textMuted,
    },
    tr: {
      transition: "background-color 0.2s",
    },
    imageContainer: {
      width: "50px",
      height: "50px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "8px",
      overflow: "hidden",
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.surfaceAlt,
    },
    tableImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    noImage: {
      color: theme.textMuted,
      fontSize: "12px",
    },
    editBtn: {
      backgroundColor: theme.info,
      color: "#fff",
      border: "none",
      padding: "6px 12px",
      borderRadius: "6px",
      marginRight: "8px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "500",
      transition: "all 0.2s",
    },
    deleteBtn: {
      backgroundColor: theme.danger,
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "500",
      transition: "all 0.2s",
    },
    noData: {
      textAlign: "center",
      padding: "60px",
      color: theme.textMuted,
      fontSize: "14px",
    },
    loading: {
      textAlign: "center",
      padding: "60px",
      color: theme.textMuted,
      fontSize: "14px",
    },
    modalOverlay: {
      position: "fixed",
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(4px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: theme.surface,
      borderRadius: "16px",
      width: "90%",
      maxWidth: "600px",
      maxHeight: "90vh",
      overflowY: "auto",
      padding: "24px",
      boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
      border: `1px solid ${theme.border}`,
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: `1px solid ${theme.border}`,
    },
    modalTitle: {
      fontSize: "20px",
      color: theme.text,
      margin: 0,
      fontWeight: "700",
    },
    closeBtn: {
      background: "none",
      border: "none",
      fontSize: "24px",
      cursor: "pointer",
      color: theme.textMuted,
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "8px",
      transition: "all 0.2s",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "600",
      color: theme.textMuted,
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    row2col: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "20px",
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      border: `1px solid ${theme.border}`,
      borderRadius: "8px",
      fontSize: "14px",
      backgroundColor: theme.surfaceAlt,
      color: theme.text,
      boxSizing: "border-box",
      transition: "all 0.2s",
      outline: "none",
    },
    textarea: {
      width: "100%",
      padding: "10px 12px",
      border: `1px solid ${theme.border}`,
      borderRadius: "8px",
      fontSize: "14px",
      fontFamily: "inherit",
      resize: "vertical",
      backgroundColor: theme.surfaceAlt,
      color: theme.text,
      boxSizing: "border-box",
      transition: "all 0.2s",
      outline: "none",
    },
    select: {
      width: "100%",
      padding: "10px 12px",
      border: `1px solid ${theme.border}`,
      borderRadius: "8px",
      fontSize: "14px",
      backgroundColor: theme.surfaceAlt,
      color: theme.text,
      transition: "all 0.2s",
      outline: "none",
      cursor: "pointer",
    },
    fileInput: {
      width: "100%",
      padding: "8px 0",
      color: theme.text,
    },
    previewImage: {
      width: "100px",
      height: "100px",
      objectFit: "cover",
      borderRadius: "8px",
      marginTop: "10px",
      border: `1px solid ${theme.border}`,
    },
    modalFooter: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      marginTop: "24px",
      paddingTop: "16px",
      borderTop: `1px solid ${theme.border}`,
    },
    cancelBtn: {
      padding: "10px 20px",
      backgroundColor: theme.surfaceAlt,
      border: `1px solid ${theme.border}`,
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.2s",
      color: theme.text,
    },
    submitBtn: {
      padding: "10px 20px",
      backgroundColor: theme.gradient,
      color: "white",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "600",
      transition: "all 0.2s",
      boxShadow: `0 4px 6px -1px ${theme.primary}30`,
    },
  };

  return (
    <div style={styles.container}>
      <style>{`
        button:hover {
          transform: translateY(-1px);
          filter: brightness(1.1);
        }
        input:focus, textarea:focus, select:focus {
          border-color: ${theme.primary} !important;
          box-shadow: 0 0 0 3px ${theme.primaryLight} !important;
        }
        tr:hover {
          background-color: ${theme.surfaceAlt};
        }
        .close-btn:hover {
          background-color: ${theme.surfaceAlt} !important;
          color: ${theme.text} !important;
        }
      `}</style>

      <div style={styles.header}>
        <h1 style={styles.title}>Press Release Management</h1>
        <button style={styles.addButton} onClick={() => { resetForm(); setShowModal(true); }}>
          + Add Press Release
        </button>
      </div>

      {/* Table */}
      <div style={styles.tableWrapper}>
        {loading ? (
          <div style={styles.loading}>Loading press releases...</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Image</th>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>RD</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Hide</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pressReleases.length === 0 ? (
                <tr>
                  <td colSpan="8" style={styles.noData}>No press releases found</td>
                </tr>
              ) : (
                pressReleases.map((item) => (
                  <tr key={item.id} style={styles.tr}>
                    <td style={styles.td}>{item.id}</td>
                    <td style={styles.td}>
                      {item.pic ? (
                        <div style={styles.imageContainer}>
                          <img 
                            src={getImageUrl(item.pic)}
                            alt={item.title}
                            style={styles.tableImage}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              if (parent && !parent.querySelector('.error-message')) {
                                const errorSpan = document.createElement('span');
                                errorSpan.className = 'error-message';
                                errorSpan.style.color = theme.textMuted;
                                errorSpan.style.fontSize = '11px';
                                errorSpan.innerText = 'No Image';
                                parent.appendChild(errorSpan);
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <span style={styles.noImage}>No image</span>
                      )}
                    </td>
                    <td style={styles.tdTitle}>{item.title}</td>
                    <td style={styles.tdDesc}>
                      {item.description?.length > 60 
                        ? `${item.description.substring(0, 60)}...` 
                        : item.description}
                    </td>
                    <td style={styles.td}>{item.rd}</td>
                    <td style={styles.td}>{getStatusBadge(item.status)}</td>
                    <td style={styles.td}>{getHideBadge(item.hide)}</td>
                    <td style={styles.td}>
                      <button style={styles.editBtn} onClick={() => handleEdit(item)}>Edit</button>
                      <button style={styles.deleteBtn} onClick={() => handleDelete(item.id)}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Popup */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{editingId ? "Edit Press Release" : "Add New Press Release"}</h2>
              <button className="close-btn" style={styles.closeBtn} onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Enter title"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Picture</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={styles.fileInput}
              />
              {preview && (
                <img 
                  src={preview} 
                  alt="Preview" 
                  style={styles.previewImage}
                />
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                style={styles.textarea}
                placeholder="Enter description"
                rows="4"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>RD (Release Date) *</label>
              <input
                type="text"
                name="rd"
                value={formData.rd}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="e.g., 2024-01-15 or custom text"
              />
            </div>

            <div style={styles.row2col}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={styles.select}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Hide</label>
                <select
                  name="hide"
                  value={formData.hide}
                  onChange={handleInputChange}
                  style={styles.select}
                >
                  <option value="0">No (Visible)</option>
                  <option value="1">Yes (Hidden)</option>
                </select>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={styles.submitBtn} onClick={handleSubmit}>
                {editingId ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}